import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { formatShortDate, APP_DEFAULT_TZ, currentLang } from '@utils/dateUtils';
import type { IMilestoneHighlight, IMilestoneSummary } from './summarizeMilestones';
import styles from './styles.module.css';

type IMilestoneHighlightsProps = { summary: IMilestoneSummary };

type IHighlightBoxProps = {
  tone: 'late' | 'onTrack' | 'next';
  title: string;
  name: string;
  status?: string;
};

/** "in 12 days" / "Due today" / "6 days late" — how far a milestone's day is from today. */
function dueLabel(t: TFunction, days: number): string {
  if (days > 0) return t('portfolio.card.highlights.inDays', { count: days });
  if (days === 0) return t('portfolio.card.highlights.dueToday');
  return t('portfolio.card.highlights.daysLate', { count: -days });
}

function HighlightBox({ tone, title, name, status }: IHighlightBoxProps) {
  return (
    <div className={styles.box} data-tone={tone} data-testid="milestone-highlight">
      <span className={styles.diamond} aria-hidden />
      <div className={styles.text}>
        <div className={styles.title}>{title}</div>
        <div className={styles.name} title={name}>{name}</div>
        {status ? <div className={styles.status}>{status}</div> : null}
      </div>
    </div>
  );
}

function currentBox(t: TFunction, current: IMilestoneHighlight, delayed: boolean) {
  const done = t('portfolio.card.highlights.pctDone', { pct: Math.round(current.progressPct) });
  return (
    <HighlightBox
      tone={delayed ? 'late' : 'onTrack'}
      title={t(delayed ? 'portfolio.card.highlights.currentDelayed' : 'portfolio.card.highlights.current')}
      name={current.name}
      status={`${delayed ? dueLabel(t, current.daysFromToday) : t('portfolio.card.highlights.onTrack')} · ${done}`}
    />
  );
}

/**
 * The current and next milestone, side by side — what the strip's dots mean
 * for this week. Replaces the old clipped "Current: …" label and the red
 * delay sentence. Nothing renders for a schedule with no milestones.
 */
export function MilestoneHighlights({ summary }: IMilestoneHighlightsProps) {
  const { t } = useTranslation();
  const lang = currentLang();
  const { current, currentDelayed, next } = summary;
  if (summary.total === 0) return null;

  if (!current) {
    return (
      <div className={styles.grid} data-testid="milestone-highlights">
        <div className={styles.span}>
          <HighlightBox tone="onTrack" title={t('portfolio.card.highlights.current')} name={t('portfolio.card.highlights.allDone')} />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.grid} data-testid="milestone-highlights">
      {currentBox(t, current, currentDelayed)}
      {next ? (
        <HighlightBox
          tone="next"
          title={t('portfolio.card.highlights.next')}
          name={next.name}
          status={`${dueLabel(t, next.daysFromToday)} · ${formatShortDate(next.dateIso, lang, APP_DEFAULT_TZ)}`}
        />
      ) : (
        <HighlightBox tone="next" title={t('portfolio.card.highlights.next')} name={t('portfolio.card.highlights.noNext')} />
      )}
    </div>
  );
}
